import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AdornedInput, FormField, FormHelp, Label } from '@zillow/constellation-10';
import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { propertyUtils } from '@zg-rentals/rental-manager-env';
import { useQueryClient } from '@zg-rentals/react-query5';

import { multiFieldsUtil, SAVE_TO_PARENT_KEY } from '../../../../utils/postingUtils';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';

import FloorPlan from '../../../../model/FloorPlan';
import WizardServerError from '../../../../model/WizardServerError';

import WizardAlert from '../../../../components/WizardAlert';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { useForm } from '../../../../hooks';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import typeCheckUtils from '../../../../utils/typeCheckUtils';
import { customConstraints } from '../../../../utils/formValidation';
import { postingConstraints } from '../../../../utils/formValidation';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import InputFieldWarningConfirmationSFModal from '../../../../components/Modal/InputFieldWarningConfirmationSFModal';

import type { ChangeEvent } from 'react';
import type { FloorPlanProps } from '../../../../model/FloorPlan';
import type { MockEventProps } from '../types';

const inputName = 'squareFeet';
const sqftWarningLimit = 20000;

const mockEvent = {
  target: {
    name: inputName,
    value: undefined,
  },
};

const SquareFootageFormField = ({ isRelistingFieldset }: { isRelistingFieldset?: boolean }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: saveMultipleFieldsPropertyInfo } = useRentalManagerUserPropertyMultiEditWebApiHandler_multi();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, isSaveComplete, isInModalState, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const {
    currentProperty: { listingAlias, floorplans },
  } = useSelector((state) => state.properties);

  const [customErrorObj, setCustomErrorObj] = useState<Record<string, unknown> | null>();
  const [showWarningConfirmationModal, setShowWarningConfirmationModal] = useState(false);

  const previousValue = useRef();
  const sqftInputRef = useRef<HTMLInputElement>(null);
  const sqftValueRef = useRef<number>(0);

  const floorplan = floorplans[0] || {};
  const { floorPlanId, roomForRent: { roomSquareFeet } = {}, squareFeet } = floorplan as FloorPlanProps;

  const label = isRoomForRent ? 'Property square footage' : 'Square footage';

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      squareFeet,
    },
    postingConstraints,
  );

  useEffect(() => {
    // @ts-expect-error
    previousValue.current = inputs[inputName];
  }, []);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: 'squareFeet',
          // @ts-expect-error
          value: inputs.squareFeet,
        },
      });
    }
  }, [isSaveComplete]);

  const clearCustomErrors = () => {
    setCustomErrorObj(null);
  };

  const clearFieldError = () => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const checkConditionOnExternalFieldChange = () => {
    const { response: aResponse, message } = isSqftMoreThanRoomSqft();

    if (aResponse === false) {
      const errorObj = {
        squareFeet: message,
      };

      setCustomErrorObj(errorObj);
    }

    return aResponse;
  };

  /**
   *
   * @returns obj { response: bool, message: string }
   */
  const isSqftMoreThanRoomSqft = () => {
    // skip validation if square feet is empty
    if (!roomSquareFeet) {
      return {
        response: true,
        message: '',
      };
    }

    return customConstraints.isSqrftGTERoomSqrft(Number(roomSquareFeet), sqftValueRef.current);
  };

  const restSaveCTAStateDueToError = () => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const save = (event: ChangeEvent<HTMLSelectElement> | MockEventProps | typeof mockEvent) => {
    const { value, name: fieldName } = event.target;
    sqftValueRef.current = Number(value);

    if (hasFieldError(fieldName, value) || checkConditionOnExternalFieldChange() === false) {
      restSaveCTAStateDueToError();

      return;
    }

    const numCastValue = typeCheckUtils.Number(value);

    if (numCastValue && numCastValue === Number(previousValue.current || 0)) {
      if (isInModalState) {
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      }
      return;
    }

    if (numCastValue && numCastValue >= sqftWarningLimit && !showWarningConfirmationModal) {
      // open confirmation modal
      setShowWarningConfirmationModal(true);
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    // set below the shouldSaveOnBlur and isSaveComplete check
    // @ts-expect-error
    previousValue.current = inputs[inputName];

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName,
      floorPlanId: floorPlanId as string,
      listingAlias,
      saveToParentKey: SAVE_TO_PARENT_KEY.FLOOR_PLANS,
      value: numCastValue,
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        const { listingOutputFieldData } = response;
        // all data properties are not returned in the response
        const mergedFloorPlan = {
          ...floorplans[0],
          ...listingOutputFieldData?.floorPlans[0],
        };

        // TODO: BE needs to sync responses on various endpoints beds, numBedrooms
        // sync mismatched keys from response
        const floorPlan = FloorPlan(mergedFloorPlan);
        floorPlan.beds = mergedFloorPlan?.numBedrooms ?? '';
        floorPlan.baths = mergedFloorPlan?.numBathrooms ?? '';

        // if squareFeet is null the key is not returned in the response
        floorPlan['squareFeet'] = String(listingOutputFieldData?.floorPlans[0]?.squareFeet ?? '');

        // make sure we pass an array
        dispatch(PropertiesActions.updateCurrentProperty({ floorplans: [floorPlan] }));

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt: displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent) => {
    clearCustomErrors();
    clearFieldError();
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  // confirmation modal related functions
  const handleModalClose = () => {
    setShowWarningConfirmationModal(false);

    mockEvent.target.value = previousValue.current;

    handleFormChange(mockEvent);

    (sqftInputRef?.current as HTMLInputElement)?.focus();
  };

  const handleModalSubmit = () => {
    // @ts-expect-error
    mockEvent.target.value = inputs[inputName];

    save(mockEvent);

    setShowWarningConfirmationModal(false);
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      <FormField
        className="SquareFootageFormField"
        label={<Label size={isRelistingFieldset ? 'lg' : 'md'}>{label}</Label>}
        // @ts-expect-error
        error={!!errorObj?.squareFeet || !!customErrorObj?.squareFeet}
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                ref={sqftInputRef}
                name={inputName}
                value={String(inputs[inputName] || '')}
                onChange={handleChange}
                onBlur={save}
                // @ts-expect-error
                error={!!errorObj.squareFeet}
                aria-label={label}
              />
            }
            endAdornment={<AdornedInput.Adornment>sq. ft.</AdornedInput.Adornment>}
          />
        }
        // @ts-expect-error
        description={<FormHelp>{errorObj?.squareFeet || customErrorObj?.squareFeet}</FormHelp>}
      />
      {showWarningConfirmationModal && (
        <InputFieldWarningConfirmationSFModal
          fieldName={inputName}
          // @ts-expect-error
          previousValue={previousValue.current}
          // @ts-expect-error
          value={inputs[inputName]}
          isModularListing={true}
          handleModalCloseML={handleModalClose}
          handleModalSubmitML={handleModalSubmit}
        />
      )}
    </>
  );
};

export default SquareFootageFormField;
